'use strict';

/**
 * Base class for the Application
 *
 * @category   Collection
 * @module     base
 * @copyright  Copyright (c) 2014, COLIDO (http://www.colido.net)
 * @author     Jonas Girnatis <dermusterknabe@gmail.com>
 */

var Backbone = require('backbone');
var Marionette = require('backbone-marionette');
var Html = require('base/Colido.Html');
var Util = require('base/Colido.Util');

var translate = require('translate');
var translations = require('messages/messages');

require('namespace');
require('config-default');

var Application = Marionette.Application.extend({
    /**
     * Namespace for html rendering functions (see Colido.Html for implementaion)
     */
    html: null,

    /**
     * Namespace for util functions (see Colido.Util for implementaion)
     */
    util: null,

    /**
     * The app type
     * @type {string} [main | share]
     */
    appType: 'main',

    keys: {
        TAB: 9,
        BACKSPACE: 8,
        LEFTARROW: 37,
        UPARROW: 38,
        RIGHTARROW: 39,
        DOWNARROW: 40,
        RETURN: 13,
        COMMA: 188,
        COMMA_SECONDARY: 110,
        ESCAPE: 27,
    },

    /**
     * Array with supported languages
     * @type {Array}
     */
    supportedLanguages: ['en', 'de'],

    /**
     * Language of the application
     * @type {string}|{null}
     */
    language: null,

    initialize: function(options) {
        Colido.config = _.extend({}, Colido.configDefault, Colido.config);
        this.config = Colido.config;

        this.version = Colido.version;
        this.commit = Colido.commit;

        this.util = Util;
        this.html = Html;

        this._initResizeTrigger();
        this._initKeyTrigger();
        this.vent.on('logged-in', this._initWebSocket.bind(this));
    },

    /**
     * Returns the proper channel of a module
     * @param {string=} name The name of the channel or if empty, the global channel.
     * @returns {object} Shorthand methods for execute, request, trigger
     */
    radio: function(name) {
        var channel = Backbone.Wreqr.radio.channel(name || 'global');

        return {
            execute: function() {
                this.commands.execute.apply(this.commands, arguments);
            }.bind(channel),
            request: function() {
                return this.reqres.request.apply(this.reqres, arguments);
            }.bind(channel),
            trigger: function() {
                this.vent.trigger.apply(this.vent, arguments);
            }.bind(channel),
            vent: channel.vent,
        }
    },

    /**
     * Indicates, if the application is the main application
     * @returns {boolean} True, if it is the main application
     */
    isMain: function() {
        return this.getOption('appType') === 'main';
    },

    /**
     * Indicates, if it's the share subapplication
     * @returns {boolean} True, if it is the share subapplication
     */
    isShare: function() {
        return this.getOption('appType') === 'share';
    },

    /**
     * Returns the proper API URL for the given endpoint (will convert inner slashes in apiVersion to "-")
     *
     * @param {string} endpoint The endpoint of the API
     * @returns {string} The full path for the API endpoint
     */
    getApiUrl: function(endpoint) {
        endpoint = endpoint ? endpoint.replace(/^\/+/, '').replace(/\/+$/, '') : null; //removes leading/trailing slashes

        var host = this.config.apiUrl.replace(/\/+$/, ''); //removes trailing slash
        var version = this.config.apiVersion.replace(/^\/+/, '').replace(/\/+$/, '').replace(/\//, '-'); //removes leading/trailing slashes converts remaing slashed to -

        var urlParts = [host];

        if (version) {
            urlParts.push(version);
        }

        if (endpoint) {
            urlParts.push(endpoint);
        }

        return urlParts.join('/');
    },

    /**
     * Convenience wrapper
     */
    addModal: function(content, options) {
        return App.rootView.addModal(content, options);
    },

    /**
     * Convenience wrapper
     */
    hasModal: function(content, options) {
        return App.rootView.hasModal();
    },

    toggleFullscreen: function() {
        return App.rootView.toggleFullscreen();
    },

    setLanguage: function(language) {
        if (this.language === language) {
            return;
        }

        var defaultLanguage = _.first(this.supportedLanguages);

        this.language = _.contains(this.supportedLanguages, language) ? language : defaultLanguage;

        this.t = translate.getTranslationFunction(translations[this.language], {
            debug: this.config.showMissingTranslations,
            namespaceSplitter: '::',
            defaultTranslations: translations[defaultLanguage],
        });
    },

    /**
     * Returns the language of the application
     * @returns {string} The ISO code of the language
     */
    getLanguage: function() {
        var defaultLanguage = _.first(this.supportedLanguages);

        return this.language || defaultLanguage;
    },

    requireData: function(collections, options) {
        collections = _.isArray(collections) ? collections : [collections];
        options = options || {};

        if (_.isFunction(options)) {
            options = {success: options};
        }

        var cancelOnNavigation = _.isUndefined(options.cancelOnNavigation) ? true : options.cancelOnNavigation;
        var currentNavigationCounter = App.router.getNavigationCounter();

        var success = options.success || function() {};

        var error = options.error || function(collection) {
            var collectionName = _.result(collection, 'name');
            Messenger().error({
                message: App.t('global::api:fetch-error', {data: collectionName}),
            });
            console.log('Failed loading:', collectionName);
        };

        var loading = options.loading || App.rootView.mainRegion.currentView.showLoader.bind(App.rootView.mainRegion.currentView)

        if (this.isDataLoaded(collections)) {
            success();
        } else {
            loading();

            collections.forEach(function(collection){
                if (!(collection instanceof Backbone.Collection)) {
                    App.config.debug && console.warn('App::requireData() only supports collections. This is not a collection:', collection);

                    return;
                }

                if (this.isDataLoaded(collection)) {
                    return;
                }

                collection.fetch({
                    // reset: true,
                    success: function() {
                        var collectionName = _.result(collection, 'name');

                        App.config.debug && console.log('Successfully fetched:', collectionName);
                        App._loadedData.push(collectionName);

                        if (cancelOnNavigation && App.hasNavigatedSince(currentNavigationCounter)) {
                            return;
                        }

                        if (App.isDataLoaded(collections)) { //all required data loaded
                            success();
                        }
                    },

                    error: function(collection) {
                        error(collection);
                    },
                })
            }.bind(this));
        }
    },

    hasNavigatedSince: function(navigationCounter) {
        return navigationCounter !== App.router.getNavigationCounter();
    },

    /**
     * Wrapper for collection fetching, will fire appropriate events
     */
    loadData: function(collection, options) {
        //@todo make jquery ajax options passable
        options = options || {};

        if (_.isUndefined(options.reload)){
            options.reload = true;
        }

        if (_.contains(this._loadedData, collection.name)) {
            if (options.reload){
                this._reloadData(collection, options);
            }
        } else {
            collection.fetch({
                reset: true,
                success: function(collection) {
                    Colido.config.debug && console.log('Successfully fetched:', collection.name);
                    App._loadedData.push(collection.name);
                    App.vent.trigger(collection.name + ':load', collection);
                },
                error: function(collection){
                    Messenger().error({
                        message: App.t('global::api:fetch-error', {data: collection.name}),
                    });
                    console.log('Failed loading:', collection.name);
                },
                data: options.data ? options.data : null,
            });

            this.stopListening(App.vent, collection.name + ':reload');
            this.listenTo(App.vent, collection.name + ':reload', function() {
                this._reloadData(collection);
            }.bind(this));
        }
    },

    /**
     * Checks if data was already loaded
     * @param {string|array} collectionNames String or array of strings with collectionName(s) to check
     * @returns {boolean} Return true if data was already loaded
     */
    isDataLoaded: function(collections) {
        collections = _.isArray(collections) ? collections : [collections];

        return collections.every(function(collection) {
            var collectionName = _.isString(collection) ? collection : _.result(collection, 'name');

            return _.contains(App._loadedData, collectionName);
        });
    },

    //Private stuff
    _loadedData: [],

    _reloadData: function(collection, options) {
        options = options || {};

        this._loadedData = _.without(this._loadedData, collection.name);

        collection.fetch({
            reset: true,
            success: function(collection) {
                Colido.config.debug && console.log('Successfully reloaded:', collection.name);
                App._loadedData.push(collection.name);
                App.vent.trigger(collection.name + ':load', collection);
            },
            error: function(collection){
                console.log('Failed reloading:', collection.name);
            },
            data: options.data ? options.data : null,
        });
    },

    _initResizeTrigger: function() {
        //trigger delayed resize event
        $(window).on('resize', function(){ this.resized = true; }.bind(this));

        window.setInterval(function(){
            if (this.resized) {
                App.vent.trigger('resizeThrottled');
                this.resized = false;
            }
        }.bind(this), 400);
    },

    _initKeyTrigger: function() {
        $(document).on('keydown', function(event) {
            App.vent.trigger('keydown', event);
        }.bind(this));

        $(document).on('keyup', function(event) {
            App.vent.trigger('keyup', event);
        }.bind(this));
    },

    _initWebSocket: function() {
        if (this.config.socketUrl) {
            try {
                this.websocket = new WebSocket(this.config.socketUrl, 'echo-protocol');

                this.websocket.onopen = function(event) {
                    App.config.debug && console.log('Successfully opened WebSocket');
                    this.socketRetries = 0;
                    this.sendToWebsocket('login', {userId: App.user.id});
                }.bind(this);

                this.websocket.onmessage = function(event) {
                    var message = JSON.parse(event.data);
                    if (message.channel && message.action) {
                        App.radio(message.channel).trigger(message.action, message.data);
                    }
                }.bind(this);

                this.websocket.onerror  = function() {
                    App.config.debug && console.log('Error: Connection to WebSocket failed');
                }.bind(this);

                this.websocket.onclose = function() {
                    App.config.debug && console.log('Error: Connection to WebSocket closed. Trying to reconnect');
                    this._reconnectWebSocket();
                }.bind(this)
            } catch(exception) {
                console.log('Error: Connection to WebSocket failed, probably due to malformed config.socketUrl');
            }
        }
    },

    _reconnectWebSocket: function(){
        this.socketRetries++;

        var timeout = 2;

        if (this.socketRetries < 5) {
            timeout = 2;
        } else if (this.socketRetries < 10) {
            timeout = 10;
        } else if (this.socketRetries < 100) {
            timeout = 100;
        } else {
            timeout = 1000;
        }

        setTimeout(this._initWebSocket.bind(this), timeout * 1000);
    },

    sendToWebsocket: function(action, data) {
        data = data || {};
        data.apiToken = App.user.get('apiToken');
        data.action = action;

        this.websocket.send(JSON.stringify(data));
    },

    /**
     * Sends a notification to the user and returns the Notification instance, if existing
     * @see https://developer.mozilla.org/de/docs/Web/API/notification
     * @see https://developer.mozilla.org/de/docs/Web/API/notification#Parameters
     * @param {string} title   The title of the notification
     * @param {object} options (Optional) Options to add. Such as body, icon -> See second link
     * @returns {Notification}|{null} The notification if successful or null
     */
    sendNotification: function(title, options) {
        options = options || {};

        var notification = null;

        if ('Notification' in window) {
            if (Notification.permission === 'granted') {
                try {
                    notification = new Notification(title, options);
                } catch (e) { /* not critical */ }
            } else if (Notification.permission !== 'denied') {
                Notification.requestPermission(function (permission) {
                    if (permission === 'granted') {
                        try {
                            notification = new Notification(title, options);
                        } catch (e) { /* not critical */ }
                    }
                });
            }
        }

        return notification;
    },
});

module.exports = Application;